import type { SharedFederatedProviderSlug } from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  ConfirmDialog,
} from '@instanexus/ui';
import { setAccessToken } from '@instanexus/web-auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath } from '@/lib/auth-paths';
import { clientAppOrigin, parseSignInRequest } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

const QUERY_KEY = ['federation', 'signInMethods'] as const;

function unlinkTitleKey(provider: SharedFederatedProviderSlug): string {
  switch (provider) {
    case 'google':
      return 'federated.methods.unlinkTitle';
    case 'microsoft':
      return 'federated.methods.unlinkMicrosoftTitle';
    case 'apple':
      return 'federated.methods.unlinkAppleTitle';
  }
}

function unlinkedKey(provider: SharedFederatedProviderSlug): string {
  switch (provider) {
    case 'google':
      return 'federated.methods.unlinked';
    case 'microsoft':
      return 'federated.methods.unlinkedMicrosoft';
    case 'apple':
      return 'federated.methods.unlinkedApple';
  }
}

type Phase = 'checking' | 'signed-out' | 'ready' | 'error' | 'partner';

/**
 * The Sign-in methods page on the central app: shows whether a password is set and which Google
 * or Microsoft account is linked, and unlinks Google or Microsoft behind a confirmation.
 */
export function SignInMethodsPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { client } = useMemo(() => parseSignInRequest(searchParams), [searchParams]);

  const [phase, setPhase] = useState<Phase>(client === 'partner' ? 'partner' : 'checking');
  const [pageErrorCode, setPageErrorCode] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<SharedFederatedProviderSlug | null>(null);
  const [unlinkedProvider, setUnlinkedProvider] = useState<SharedFederatedProviderSlug | null>(null);

  const check = useCallback(async (): Promise<void> => {
    setPhase('checking');
    setPageErrorCode(null);
    try {
      const session = await api.auth.refreshSession();
      if (session === undefined) {
        setPhase('signed-out');
        return;
      }
      setAccessToken(session.accessToken);
      setPhase('ready');
    } catch (error: unknown) {
      setPageErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
      setPhase('error');
    }
  }, [api]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current || client === 'partner') return;
    started.current = true;
    void check();
  }, [check, client]);

  const methods = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.federation.signInMethods(),
    enabled: phase === 'ready',
  });

  // Fails closed: while loading or on error, social sign-in counts as off (S53-5emp). The auth app has no AuthProvider.
  const releaseFlags = useQuery({
    queryKey: ['release-flags', 'sign-in-methods'],
    queryFn: async () => (await api.auth.releaseFlags()).flags,
    enabled: phase === 'ready',
    staleTime: 60_000,
    retry: false,
  });
  const offersSocial = releaseFlags.data?.social_sign_in === true;

  const unlink = useMutation({
    mutationFn: (provider: SharedFederatedProviderSlug) => api.federation.unlink(provider),
    onSuccess: async (_data, provider) => {
      setUnlinkTarget(null);
      setUnlinkedProvider(provider);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const closeDialog = (open: boolean): void => {
    if (!open) {
      setUnlinkTarget(null);
      const refused = unlink.isError;
      unlink.reset();
      if (refused) void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    }
  };

  if (phase === 'partner') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardDescription>{t('federated.methods.partnerUnavailable')}</CardDescription>
          </CardHeader>
        </Card>
      </AuthCardLayout>
    );
  }

  if (phase === 'signed-out') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('federated.methods.signedOutTitle')}
            </h1>
          </CardHeader>
          <CardFooter>
            <Button asChild>
              <Link to={centralLoginPath(client)}>{t('federated.methods.signIn')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  if (phase === 'error') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('central.unavailableTitle')}
            </h1>
            <CardDescription role="alert">
              {errorMessage(t, pageErrorCode ?? UNEXPECTED_FAILURE)}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              type="button"
              onClick={() => {
                void check();
              }}
            >
              {t('central.retry')}
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  if (phase === 'ready' && methods.isError) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('federated.methods.loadFailedTitle')}
            </h1>
            <CardDescription role="alert">
              {errorMessage(t, isApiError(methods.error) ? methods.error.code : UNEXPECTED_FAILURE)}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              type="button"
              onClick={() => {
                void methods.refetch();
              }}
            >
              {t('central.retry')}
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  if (phase === 'ready' && methods.data !== undefined && !releaseFlags.isPending) {
    const google = methods.data.providers.find((p) => p.provider === 'google');
    const microsoft = methods.data.providers.find((p) => p.provider === 'microsoft');
    const apple = methods.data.providers.find((p) => p.provider === 'apple');
    const targetEmail =
      unlinkTarget === null ? undefined : { google, microsoft, apple }[unlinkTarget]?.email;
    const origin = clientAppOrigin(client);
    const backUrl = origin === null ? null : `${origin}${client === 'admin' ? '/account' : '/'}`;
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('federated.methods.title')}
            </h1>
            <CardDescription>{t('federated.methods.description')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {unlinkedProvider !== null ? (
              <Alert role="status">
                <AlertDescription>
                  {t(unlinkedKey(unlinkedProvider))}
                </AlertDescription>
              </Alert>
            ) : null}
            <dl className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <dt className="font-medium">{t('federated.methods.password')}</dt>
                <dd className="text-muted-foreground">
                  {methods.data.password
                    ? t('federated.methods.passwordSet')
                    : t('federated.methods.passwordNotSet')}
                </dd>
              </div>
              {google !== undefined || offersSocial ? (
                <div className="flex flex-col gap-1">
                  <dt className="font-medium">{t('federated.methods.google')}</dt>
                  <dd className="text-muted-foreground">
                    {google !== undefined ? (
                      t('federated.methods.linkedAs', { email: google.email })
                    ) : (
                      <>
                        <span>{t('federated.methods.notLinked')}</span>
                        <span className="block text-sm">{t('federated.methods.notLinkedHint')}</span>
                      </>
                    )}
                  </dd>
                </div>
              ) : null}
              {microsoft !== undefined ? (
                <div className="flex flex-col gap-1">
                  <dt className="font-medium">{t('federated.methods.microsoft')}</dt>
                  <dd className="text-muted-foreground">
                    {t('federated.methods.linkedAs', { email: microsoft.email })}
                  </dd>
                </div>
              ) : null}
              {apple !== undefined ? (
                <div className="flex flex-col gap-1">
                  <dt className="font-medium">{t('federated.methods.apple')}</dt>
                  <dd className="text-muted-foreground">
                    {t('federated.methods.linkedAs', { email: apple.email })}
                  </dd>
                </div>
              ) : null}
            </dl>
            {google !== undefined ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUnlinkedProvider(null);
                    setUnlinkTarget('google');
                  }}
                >
                  {t('federated.methods.unlink')}
                </Button>
              </div>
            ) : null}
            {microsoft !== undefined ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUnlinkedProvider(null);
                    setUnlinkTarget('microsoft');
                  }}
                >
                  {t('federated.methods.unlinkMicrosoft')}
                </Button>
              </div>
            ) : null}
            {apple !== undefined ? (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUnlinkedProvider(null);
                    setUnlinkTarget('apple');
                  }}
                >
                  {t('federated.methods.unlinkApple')}
                </Button>
              </div>
            ) : null}
          </CardContent>
          {backUrl !== null ? (
            <CardFooter>
              <Button asChild variant="link">
                <a href={backUrl}>{t('federated.methods.back')}</a>
              </Button>
            </CardFooter>
          ) : null}
        </Card>
        <ConfirmDialog
          open={unlinkTarget !== null}
          onOpenChange={closeDialog}
          title={t(unlinkTarget === null ? unlinkTitleKey('google') : unlinkTitleKey(unlinkTarget))}
          description={t('federated.methods.unlinkDescription', { email: targetEmail ?? '' })}
          confirmLabel={t('federated.methods.unlinkConfirm')}
          confirmingLabel={t('federated.methods.unlinking')}
          cancelLabel={t('federated.methods.cancel')}
          closeLabel={t('federated.methods.close')}
          confirmVariant="destructive"
          isPending={unlink.isPending}
          onConfirm={() => {
            if (unlinkTarget !== null) unlink.mutate(unlinkTarget);
          }}
          error={
            unlink.isError
              ? {
                  message: errorMessage(
                    t,
                    isApiError(unlink.error) ? unlink.error.code : UNEXPECTED_FAILURE,
                  ),
                  reference: null,
                }
              : undefined
          }
        />
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <p role="status" className="text-muted-foreground">
            {t('central.checking')}
          </p>
        </CardHeader>
      </Card>
    </AuthCardLayout>
  );
}
